const MINIMUM_QUERY_LENGTH = 3;
const SEARCH_DELAY_MS = 180;

export function escapeSearchPhrase(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function buildSearchQuery(term, vendor = '') {
  const normalizedTerm = String(term).trim();
  if (!vendor) return normalizedTerm;
  return `"${escapeSearchPhrase(normalizedTerm)}" AND vendor:"${escapeSearchPhrase(vendor)}"`;
}

function localeProductUrl(rawUrl, rootUrl, origin) {
  const url = new URL(rawUrl, origin);
  if (url.origin !== origin) return '#';
  const normalizedRoot = rootUrl === '/' ? '/' : `/${String(rootUrl).replace(/^\/+|\/+$/g, '')}/`;
  if (normalizedRoot !== '/' && !url.pathname.startsWith(normalizedRoot)) {
    url.pathname = `${normalizedRoot.replace(/\/$/, '')}${url.pathname.startsWith('/') ? '' : '/'}${url.pathname}`;
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

class AdasateeCategorySearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-search-input]');
    this.select = this.querySelector('[data-category-select]');
    this.panel = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.list = this.querySelector('[data-search-list]');
    this.activeIndex = -1;
    this.requestSequence = 0;

    if (!this.form || !this.input || !this.select || !this.panel || !this.status || !this.list) return;
    this.input.addEventListener('input', () => this.scheduleSearch());
    this.input.addEventListener('keydown', event => this.onKeyDown(event));
    this.select.addEventListener('change', () => this.scheduleSearch());
    this.form.addEventListener('formdata', event => {
      event.formData.set('q', buildSearchQuery(this.input.value, this.select.value));
      event.formData.set('type', 'product');
      event.formData.set('options[prefix]', 'last');
    });
  }

  disconnectedCallback() {
    clearTimeout(this.searchTimer);
    this.controller?.abort();
  }

  scheduleSearch() {
    clearTimeout(this.searchTimer);
    this.controller?.abort();
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    if (this.input.value.trim().length < MINIMUM_QUERY_LENGTH) {
      this.closeResults();
      return;
    }
    this.searchTimer = setTimeout(() => this.fetchSuggestions(), SEARCH_DELAY_MS);
  }

  async fetchSuggestions() {
    const sequence = ++this.requestSequence;
    this.controller = new AbortController();
    this.openResults(this.dataset.loadingText);
    try {
      const url = new URL(this.dataset.predictiveUrl, window.location.origin);
      url.searchParams.set('q', buildSearchQuery(this.input.value, this.select.value));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.controller.signal });
      if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
      const payload = await response.json();
      if (sequence !== this.requestSequence) return;
      const products = payload?.resources?.results?.products;
      this.renderProducts(Array.isArray(products) ? products : []);
    } catch (error) {
      if (error.name === 'AbortError' || sequence !== this.requestSequence) return;
      this.list.replaceChildren();
      this.openResults(this.dataset.errorText);
    }
  }

  renderProducts(products) {
    this.list.replaceChildren();
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    if (products.length === 0) {
      this.openResults(this.dataset.noResultsText);
      return;
    }

    this.status.textContent = '';
    products.slice(0, 10).forEach((product, index) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'none');
      const link = document.createElement('a');
      link.id = `${this.panel.id}-option-${index}`;
      link.className = 'adasatee-category-search__option';
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.tabIndex = -1;
      link.href = localeProductUrl(product.url || '#', this.dataset.rootUrl || '/', window.location.origin);

      const imageSource = product.featured_image?.url || product.image;
      if (imageSource) {
        const imageUrl = new URL(imageSource, window.location.origin);
        if (imageUrl.protocol === 'https:' || imageUrl.origin === window.location.origin) {
          const image = document.createElement('img');
          image.className = 'adasatee-category-search__image';
          image.src = imageUrl.href;
          image.alt = product.featured_image?.alt || product.title || '';
          image.width = 56;
          image.height = 56;
          image.loading = 'lazy';
          link.append(image);
        }
      }

      const meta = document.createElement('span');
      meta.className = 'adasatee-category-search__meta';
      const title = document.createElement('span');
      title.className = 'adasatee-category-search__title';
      title.textContent = product.title || '';
      const price = document.createElement('span');
      price.className = 'adasatee-category-search__price';
      price.textContent = product.price || '';
      meta.append(title, price);
      link.append(meta);
      item.append(link);
      this.list.append(item);
    });
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  openResults(message = '') {
    this.status.textContent = message || '';
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  closeResults() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  onKeyDown(event) {
    const options = [...this.list.querySelectorAll('[role="option"]')];
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeResults();
      this.input.focus();
      return;
    }
    if (this.panel.hidden || options.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false'));
      this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id);
      options[this.activeIndex].scrollIntoView({ block: 'nearest' });
      return;
    }
    if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      options[this.activeIndex].click();
    }
  }
}

if (!customElements.get('adasatee-category-search')) {
  customElements.define('adasatee-category-search', AdasateeCategorySearch);
}
